/**
 * useSentenceAnalysis Hook
 *
 * Fetches sentence-level alignment analysis for pairwise model comparison.
 * Used by LensComparisonPanel to highlight shared/unique/conflict content.
 *
 * Part of: Heatmap Consensus Enhancements (Task 2)
 */

import { useState, useEffect } from 'react';

export interface SentenceLabel {
  id: string;
  text: string;
  label: 'shared' | 'unique' | 'conflict';
  side: 'A' | 'B' | 'both';
}

export interface SentenceAnalysisData {
  sentencesA: SentenceLabel[];
  sentencesB: SentenceLabel[];
  analysisMethod: 'heuristic' | 'ai';
  confidence: number;
}

interface UseSentenceAnalysisResult {
  data: SentenceAnalysisData | null;
  loading: boolean;
  error: string | null;
}

/**
 * Fetch sentence-level analysis from backend API
 *
 * @param comparisonId - Comparison ID
 * @param criterion - Criterion name (e.g., 'factual_accuracy')
 * @param modelA - Model key (providerId:modelId)
 * @param modelB - Model key (providerId:modelId)
 * @param enabled - Whether to fetch (controlled by toggle)
 * @returns Sentence analysis data, loading state, and error
 */
export function useSentenceAnalysis(
  comparisonId: string | undefined,
  criterion: string,
  modelA: string,
  modelB: string,
  enabled: boolean
): UseSentenceAnalysisResult {
  const [data, setData] = useState<SentenceAnalysisData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Skip if disabled or missing params
    if (!enabled || !comparisonId || !criterion || !modelA || !modelB) {
      console.log('[useSentenceAnalysis] Skipping fetch:', {
        enabled,
        hasComparisonId: !!comparisonId,
        hasCriterion: !!criterion,
        hasModelA: !!modelA,
        hasModelB: !!modelB
      });
      setData(null);
      setLoading(false);
      setError(null);
      return;
    }

    const fetchSentenceAnalysis = async () => {
      setLoading(true);
      setError(null);

      try {
        const params = new URLSearchParams({
          criterion,
          modelA,
          modelB
        });

        const url = `/api/results/comparisons/${comparisonId}/sentence-analysis?${params.toString()}`;

        console.log('[useSentenceAnalysis] Fetching:', {
          url,
          comparisonId,
          criterion,
          modelA,
          modelB,
          timestamp: new Date().toISOString()
        });

        const startTime = performance.now();
        const response = await fetch(url);
        const duration = performance.now() - startTime;

        console.log('[useSentenceAnalysis] Response received:', {
          status: response.status,
          statusText: response.statusText,
          ok: response.ok,
          duration: `${duration.toFixed(2)}ms`,
          headers: {
            contentType: response.headers.get('content-type'),
            contentLength: response.headers.get('content-length')
          }
        });

        if (!response.ok) {
          const errorText = await response.text();
          console.error('[useSentenceAnalysis] Error response body:', errorText);
          throw new Error(`Failed to fetch sentence analysis: ${response.status} ${response.statusText}`);
        }

        const json = await response.json();

        console.log('[useSentenceAnalysis] Parsed JSON:', {
          success: json.success,
          hasData: !!json.data,
          dataKeys: json.data ? Object.keys(json.data) : []
        });

        if (!json.success || !json.data) {
          throw new Error('Invalid response format');
        }

        setData(json.data);
        console.log('[useSentenceAnalysis] Success:', {
          sentencesA: json.data.sentencesA?.length || 0,
          sentencesB: json.data.sentencesB?.length || 0,
          method: json.data.analysisMethod
        });
      } catch (err) {
        console.error('[useSentenceAnalysis] Error:', {
          error: err,
          message: err instanceof Error ? err.message : 'Unknown error',
          stack: err instanceof Error ? err.stack : undefined
        });
        setError(err instanceof Error ? err.message : 'Unknown error');
        setData(null);
      } finally {
        setLoading(false);
      }
    };

    fetchSentenceAnalysis();
  }, [comparisonId, criterion, modelA, modelB, enabled]);

  return { data, loading, error };
}
